/**
 * VideoDemoSection
 *
 * Embeds a demo video (Loom or YouTube) in a responsive 16:9 frame.
 * Reads the URL from REACT_APP_DEMO_VIDEO_URL env var by default.
 *
 * Renders nothing if no URL is configured — keeps the Hero clean until
 * a demo video is recorded. To activate: set the env var and redeploy.
 *
 * Supported URLs:
 *  - Loom embed: https://www.loom.com/embed/<id>
 *  - YouTube embed: https://www.youtube.com/embed/<id>
 *  - Loom share link (auto-converted): https://www.loom.com/share/<id>
 *  - YouTube watch link (auto-converted): https://www.youtube.com/watch?v=<id>
 */

import React from 'react';
import { Box, alpha } from '@mui/material';
import { env } from '../../config/environment';
import { trackEvent } from '../../utils/analytics';
import { landingColors } from '../../theme/landingTheme';

interface VideoDemoSectionProps {
  url?: string;
  variant?: 'hero' | 'standalone';
}

function normalizeEmbedUrl(rawUrl: string): string | null {
  if (!rawUrl) return null;

  // Loom share link → embed
  const loomMatch = rawUrl.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;

  // YouTube watch link → embed
  const ytMatch = rawUrl.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  // youtu.be short link → embed
  const ytShortMatch = rawUrl.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (ytShortMatch) return `https://www.youtube.com/embed/${ytShortMatch[1]}`;

  // Already an embed URL
  if (rawUrl.includes('/embed/')) return rawUrl;

  // Unknown format — return as-is and let the iframe try
  return rawUrl;
}

const VideoDemoSection: React.FC<VideoDemoSectionProps> = ({
  url,
  variant = 'standalone',
}) => {
  const sourceUrl = url || env.tracking.demoVideoUrl;
  const embedUrl = normalizeEmbedUrl(sourceUrl);

  if (!embedUrl) return null;

  const isHero = variant === 'hero';

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: isHero ? 720 : 960,
        mx: isHero ? 0 : 'auto',
        mt: isHero ? 4 : 0,
        mb: isHero ? 2 : 0,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: `0 20px 60px ${alpha(landingColors.neutral.gray900, 0.4)}`,
        border: isHero
          ? `1px solid ${alpha(landingColors.neutral.white, 0.2)}`
          : `1px solid ${alpha(landingColors.neutral.gray300, 0.6)}`,
        // 16:9 aspect ratio
        '&::before': {
          content: '""',
          display: 'block',
          paddingTop: '56.25%',
        },
      }}
      onClick={() =>
        trackEvent('video_play', {
          source: isHero ? 'hero_demo' : 'standalone_demo',
        })
      }
    >
      <Box
        component="iframe"
        src={embedUrl}
        title="Demonstração Clinafy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </Box>
  );
};

export default VideoDemoSection;
